import { TreeManipulation } from './treeManipulation';
import { ModelTreeNode, ParsedTreeNodeType } from '../data/modelOptionsTree';
import { TreeSelections } from '../data/treeSelection';

//-------------------------------------------------------------------------
// class TreeStateInfo
// This class should look at the tree, and according to the
// tree selection, determine if:
//    - a node can be selected or not.
//    - a node is fully selected or not.
//-------------------------------------------------------------------------

export class TreeStateInfo {

  public static canOptionNodeBeSelected(
    nodePath : string,
    tree: ModelTreeNode,
    treeSelections : TreeSelections,
  ) {

    // Split the path into parts
    const nodePathParts = nodePath.split('/');
    nodePathParts.pop();

    while (nodePathParts.length >= 1) {

      // Build a path to the upper node
      const upperNodePath = nodePathParts.join('/');

      // Get the upper node

      const upperNode = TreeManipulation.findNodeInTree(
        tree,
        undefined,
        undefined,
        (treeNode) => treeNode.path === upperNodePath,
      );

      if (upperNode === undefined || upperNode.node === undefined) {

        // Something went wrong
        return false;
      }

      if (upperNode.node.type === ParsedTreeNodeType.Group
      || upperNode.node.type === ParsedTreeNodeType.Root
      || upperNode.node.type === ParsedTreeNodeType.Domain) {

        // This type of node will not be selected
        // continue to the upper node

      } else {

        // Since this node is selectable, we need to check if
        // there's some other node that is selecting us

        const parentNodeSelection = treeSelections.find(
          (treeSelection) => treeSelection.value === upperNodePath,
        );

        if (parentNodeSelection === undefined) {

          // We don't have a node selecting us at the moment.
          // We should still check if the parent's selection is valid.
          // If the parent's selection is not valid, we could switch to this node instead

          // Get the parent node path
          const parentPath = this.getParentPath(upperNodePath);

          if (this.isTreeSelectionStillValid(parentPath, tree, treeSelections) === true) {

            // The selection was still valid.
            // We cannot select this node
            return false;
          }
        }
      }

      // Remove the last item from nodePathParts
      nodePathParts.pop();
    }

    // If we reach here, all is good. All the selections done in the tree, make this node selectable
    return true;
  }

  public static isTreeSelectionStillValid(
    path : string,
    tree: ModelTreeNode,
    treeSelections : TreeSelections,
  ) : boolean {

    // Get the selection
    const selection = treeSelections.find((treeSelection) => treeSelection.path === path);

    if (selection === undefined) {
      // We don't have a selection.
      // This means that the selection is invalid (?)
      return false;
    }

    const selectedNodePath = selection.value;

    // Check if the node is still in the tree
    const selectedNode = TreeManipulation.findNodeInTree(
      tree,
      undefined,
      undefined,
      (treeNode) => treeNode.path === selectedNodePath,
    );

    if (selectedNode === undefined || selectedNode.node === undefined) {

      // The selection in the tree is not considered valid
      return false;
    }

    // The selection is valid
    return true;
  }

  public static isNodeFullPathSelected(
    nodePath : string,
    tree: ModelTreeNode,
    treeSelections : TreeSelections,
  ) : boolean {

    // Split the path into parts
    const nodePathParts = nodePath.split('/');
    nodePathParts.pop();

    while (nodePathParts.length >= 1) {

      // Build a path to the upper node
      const upperNodePath = nodePathParts.join('/');

      // Get the upper node

      const upperNode = TreeManipulation.findNodeInTree(
        tree,
        undefined,
        undefined,
        (treeNode) => treeNode.path === upperNodePath,
      );

      if (upperNode === undefined || upperNode.node === undefined) {

        // Something went wrong
        return false;
      }

      if (upperNode.node.type === ParsedTreeNodeType.Group
      || upperNode.node.type === ParsedTreeNodeType.Root
      || upperNode.node.type === ParsedTreeNodeType.Domain) {

        // This type of node will not be selected
        // continue to the upper node

      } else {

        // This node should be present in the selection
        // Search treeSelections, to see if this node is selected

        const parentNodeSelection = treeSelections.find(
          (treeSelection) => treeSelection.value === upperNodePath,
        );

        if (parentNodeSelection === undefined) {

          // eslint-disable-next-line no-console
          // console.log('Full node path is not selected', upperNodePath);
          return false;
        }
      }

      // Remove the last item from nodePathParts
      nodePathParts.pop();
    }

    // If we reach here, all is good. All the selections in the tree lead us to this node
    return true;
  }

  private static getParentPath(nodePath : string) : string {
    const nodePathParts = nodePath.split('/');
    nodePathParts.pop();
    return nodePathParts.join('/');
  }

  public static isNodeSelected(
    nodePath : string,
    tree: ModelTreeNode,
    treeSelections : TreeSelections,
  ) : boolean {
    // We'll check if the parent is selecting this node
    const parentPath = this.getParentPath(nodePath);

    // Check if this node is selected by the parent
    const nodeSelection = treeSelections.find(
      (treeSelection) => treeSelection.path === parentPath && treeSelection.value === nodePath,
    );

    if (nodeSelection === undefined) {
      // The parent is selecting this node
      return false;
    }

    // Check also the full path
    return this.isNodeFullPathSelected(nodePath, tree, treeSelections);
  }
}
